import { useGetCentralBranches } from '@agos/api-client/platform'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { getBranchPref, revealApp, useBranchStore } from '@agos/customer-core'

import { BranchPicker } from './BranchPicker'
import { gateAction, registryOf } from './branches'

/**
 * The half of the branch gate that runs after the bundle.
 *
 * `index.html` has already redirected a returning visitor and hidden `#root` behind a splash. This asks
 * `central/branches` who the brand's branches are and finishes the decision — reveal, ask, or redirect.
 *
 * **`revealApp()` runs on every path out of here, including the failures.** The splash has a 2 500 ms
 * backstop in the inline script precisely because a bundle can fail to boot; a bundle that boots and
 * then forgets to reveal leaves a spinner over a working app for two and a half seconds, which looks
 * exactly like the thing the backstop exists to prevent.
 *
 * **The decision is derived, not stored.** An effect that called `setState` would re-render into the
 * same decision on every registry change — the cascading-render shape the shell's own lint rule refuses,
 * and it caught this one.
 */
export function BranchGate({ children }: { children: ReactNode }) {
  const [chosen, setChosen] = useState(false)
  const pickerOpen = useBranchStore((state) => state.pickerOpen)

  const { data, isLoading, isError } = useGetCentralBranches(undefined, {
    query: { staleTime: Number.POSITIVE_INFINITY, retry: 1 },
  })

  const registry = useMemo(() => registryOf(data), [data])

  const action = useMemo(() => {
    if (isLoading) return { kind: 'wait' as const }
    if (isError) return { kind: 'reveal' as const }
    return gateAction(
      registry,
      getBranchPref(),
      typeof location === 'undefined' ? '' : location.hostname,
      typeof location !== 'undefined' && location.search.includes('fkb='),
    )
  }, [isLoading, isError, registry])

  useEffect(() => {
    if (action.kind === 'wait') return
    if (action.kind === 'redirect') {
      location.replace(action.to)
      return
    }
    revealApp()
  }, [action])

  // Two ways in, and the difference is whether it can be dismissed: the gate asking a first-time
  // visitor, or the customer reopening it from the header chip or the footer link.
  const asking = action.kind === 'ask' && !chosen
  const mode = asking ? ('welcome' as const) : ('change' as const)

  return (
    <>
      {children}

      {(asking || pickerOpen) && (
        <BranchPicker registry={registry} mode={mode} onChosen={() => setChosen(true)} />
      )}
    </>
  )
}

// `ShellPlaceholder` was here and is deleted — §9.35, 2026-08-06. Scaffolding that shipped: it stood
// in until the first real page landed, and said so on screen — *ďalšie obrazovky pribúdajú podľa
// špecifikácií v docs/pages/customer*. `HomePage` is that page and nothing has rendered this since.
//
// **It carried `data-customer-home`, and so does the real home.** Two e2e assert that hook is
// visible; had this ever been rendered, a test proving the customer app works would have been
// satisfied by a placeholder announcing that the app is unfinished. One hook, one component — the
// same reason §9.35 exists, applied to a `data-*` rather than a function.
